// src/components/forms/shared/modal/CurrentPriceList.tsx

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';
import { LocalOffer as PriceListIcon } from '@mui/icons-material';
import { CloseButton } from '../../../common/Buttons';
import LoadingSpinner from '../../../common/LoadingSpinner';
import {
  referenceDataAPI,
  PetSize,
  SystemSettings,
} from '../../../../services/api';
import ModalBase from '../../../common/modal/ModalBase';

interface CurrentPriceListProps {
  open: boolean;
  onClose: () => void;
}

const PriceListContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  border: `2px solid ${theme.palette.dividerContrast}`,
  borderRadius: theme.shape.borderRadius,
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
  },
  '& .MuiTableCell-head': {
    fontWeight: 700,
    fontSize: '1.4rem',
    padding: theme.spacing(1),
    color: '#ffffff',
  },
  '& .MuiTableCell-body': {
    padding: theme.spacing(1),
    fontSize: '1.8rem',
  },
  '& .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[50],
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
  },
}));

const PriceCellValue = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  textAlign: 'right',
}));

const HeaterLabel = styled(Typography)(() => ({
  fontSize: '1.8rem',
}));

const HeaterValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginLeft: theme.spacing(1),
}));

const HeaterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '1rem',
  padding: '0 2px',
  borderTop: `3px dashed ${theme.palette.dividerContrast}`,
  borderBottom: `3px dashed ${theme.palette.dividerContrast}`,
}));

const CurrentPriceList: React.FC<CurrentPriceListProps> = ({
  open,
  onClose,
}) => {
  const [petSizes, setPetSizes] = useState<PetSize[]>([]);
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sortedSizes = useMemo(
    () =>
      [...petSizes].sort((a, b) =>
        a.petTypeFk !== b.petTypeFk ? a.petTypeFk - b.petTypeFk : a.id - b.id
      ),
    [petSizes]
  );

  useEffect(() => {
    if (!open) return;

    const fetchPriceData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [sizes, settings] = await Promise.all([
          referenceDataAPI.getPetSizes(),
          referenceDataAPI.getSystemSettings(),
        ]);

        setPetSizes(sizes);
        setSystemSettings(settings);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load pricing data'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPriceData();
  }, [open]);

  const footerContent = (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <CloseButton onClick={onClose} minWidth="192px" />
    </Box>
  );

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      desktopWidth="400px"
      mobileWidth="92vw"
      headerIcon={<PriceListIcon />}
      headerTitle="Current Price List"
      maxHeight="600px"
      footer={footerContent}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <PriceListContainer>
          <Typography color="error">{error}</Typography>
        </PriceListContainer>
      ) : (
        <PriceListContainer>
          {systemSettings && (
            <HeaterRow>
              <HeaterLabel>Heater:</HeaterLabel>
              <HeaterValue>
                R{systemSettings.heaterDailyRate.toFixed(2)}
              </HeaterValue>
            </HeaterRow>
          )}
          <div>
            <SectionTitle>Pets by Size</SectionTitle>
            <StyledTableContainer>
              <StyledTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Size</TableCell>
                    <TableCell align="right">Single</TableCell>
                    <TableCell align="right">Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSizes.map((size) => (
                    <TableRow key={size.id}>
                      <TableCell>{size.size}</TableCell>
                      <PriceCellValue align="right">
                        {size.priceSingle
                          ? `R${size.priceSingle.toFixed(2)}`
                          : '—'}
                      </PriceCellValue>
                      <PriceCellValue align="right">
                        {size.priceSharing
                          ? `R${size.priceSharing.toFixed(2)}`
                          : '—'}
                      </PriceCellValue>
                    </TableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </StyledTableContainer>
          </div>

          <div>
            <SectionTitle>Grooming</SectionTitle>
            <StyledTableContainer>
              <StyledTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Size</TableCell>
                    <TableCell align="right">Smooth</TableCell>
                    <TableCell align="right">Long</TableCell>
                    <TableCell align="right">Cut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSizes
                    .filter(
                      (size) =>
                        size.priceGroomingSmooth ||
                        size.priceGroomingLong ||
                        size.priceCut
                    )
                    .map((size) => (
                      <TableRow key={size.id}>
                        <TableCell>{size.size}</TableCell>
                        <PriceCellValue align="right">
                          {size.priceGroomingSmooth
                            ? `R${size.priceGroomingSmooth.toFixed(2)}`
                            : '—'}
                        </PriceCellValue>
                        <PriceCellValue align="right">
                          {size.priceGroomingLong
                            ? `R${size.priceGroomingLong.toFixed(2)}`
                            : '—'}
                        </PriceCellValue>
                        <PriceCellValue align="right">
                          {size.priceCut ? `R${size.priceCut.toFixed(2)}` : '—'}
                        </PriceCellValue>
                      </TableRow>
                    ))}
                </TableBody>
              </StyledTable>
            </StyledTableContainer>
          </div>
        </PriceListContainer>
      )}
    </ModalBase>
  );
};

export default CurrentPriceList;
